import axios from "axios";
import _ from "lodash";

const formatDate = (stringDate, withTime = false) => {
  const date = new Date(stringDate);
  // Format the date object into the desired format (DD/MM/YYYY)

  const dateFormat = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  };

  if (withTime) {
    dateFormat["hour"] = "2-digit";
    dateFormat["minute"] = "2-digit";
  }

  const formattedDate = date.toLocaleDateString("en-GB", dateFormat);
  return formattedDate;
};

const formatContact = (number) => {
  // Convert the number to a string
  let str = number.toString();

  // Ensure the string is 10 digits long
  if (str.length !== 10) {
    str = "6845550102";
  }

  // Format the string
  const formatted = `(${str.slice(0, 3)}) ${str.slice(3, 6)}-${str.slice(6)}`;

  return formatted;
};

function numbersFormatter(str) {
  if (!str || typeof str !== "string") {
    return "N/A";
  }
  // Remove leading/trailing whitespace
  str = str.trim();

  // Check if empty string
  if (str === "") {
    return "N/A";
  }

  // Attempt conversion to number using parseFloat
  const number = parseFloat(str);

  // Check if conversion was successful (not NaN)
  if (isNaN(number)) {
    return "N/A";
  }

  // Return the number with two decimal places
  return number.toFixed(2);
}

function getFixedNumber(str) {
  // Remove leading/trailing whitespace
  let number = str;
  if (typeof str === "string") {
    str = str.trim();
    // Check if empty string
    if (str === "") {
      return 0;
    }
    // Attempt conversion to number using parseFloat
    number = parseFloat(str);
  }

  // Check if no value
  if (!number) {
    return 0;
  }

  // Check if conversion was successful (not NaN)
  if (isNaN(number)) {
    return 0;
  }

  // Return the number with two decimal places
  return number.toFixed(2);
}

const getDollarValue = (value, currency, conversionRate) => {
  let res = "-";
  const finalValue = numbersFormatter(value);
  if (finalValue !== "N/A") {
    if (currency === "USD") {
      res = Number(finalValue).toFixed(2);
    } else if (currency === "GBP") {
      res = (finalValue * conversionRate?.poundForDollar).toFixed(2);
    } else if (currency === "EUR") {
      res = (finalValue * conversionRate?.euroForDollar).toFixed(2);
    }
  }
  return res;
};

const getPoundValue = (value, currency, conversionRate) => {
  let res = "-";
  const finalValue = numbersFormatter(value);
  if (finalValue !== "N/A") {
    if (currency === "USD") {
      res = (finalValue * conversionRate?.dollarForPound).toFixed(2);
    } else if (currency === "GBP") {
      res = Number(finalValue).toFixed(2);
    } else if (currency === "EUR") {
      res = (finalValue * conversionRate?.euroForPound).toFixed(2);
    }
  }
  return res;
};

const getEuroValue = (value, currency, conversionRate) => {
  let res = "-";
  const finalValue = numbersFormatter(value);
  if (finalValue !== "N/A") {
    if (currency === "USD") {
      res = (finalValue * conversionRate?.dollarForEuro).toFixed(2);
    } else if (currency === "EUR") {
      res = Number(finalValue).toFixed(2);
    } else if (currency === "GBP") {
      res = (finalValue * conversionRate?.poundForEuro).toFixed(2);
    }
  }
  return res;
};

const getAbvValue = (value) => {
  if (value && typeof value === "string") {
    const num = Number(value);
    return isNaN(num) ? "-" : `${num}%`;
  } else {
    return "-";
  }
};

function truncateString(str) {
  if (str.length > 25) {
    return str.substring(0, 25) + "...";
  } else {
    return str;
  }
}

const AI_CHAT_SENDERS = ["ai", "dr"];

const isUserChatSender = (sender) => !AI_CHAT_SENDERS.includes(sender);

// Resume history may tag user rows as a display name, "User", or "user".
// Live chat uses the current display name for the user and "ai" / "dr" for the assistant.
const toLiveChatHistory = (messages, userName, aiSender) => {
  if (!Array.isArray(messages)) return [];
  return messages.map((msg) => ({
    ...msg,
    sender: isUserChatSender(msg.sender) ? userName : aiSender,
  }));
};

const isHTML = (str) => {
  // Check if string contains HTML tags
  const htmlTagRegex = /<[^>]+>/;
  return htmlTagRegex.test(str);
}

function formatHTMLResponse(text) {
  if (!text) return text;
  
  // If it's HTML, wrap it with the chat container class
  if (isHTML(text)) {
    return `<div class="chat-container">${text}</div>`;
  }
  
  // If it's not HTML, return as is
  return text;
}

async function translateText(text) {
  try {
    const res = await axios.get(
      `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|ar`
    );

    if (res.status !== 200) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }

    return res.data.responseData.translatedText;
  } catch (error) {
    console.error("Translation failed:", error);
    return text; // fallback to original text
  }
}

const getLanguageCode = (language) => {
  language = _.lowerCase(language);
  const arabicLangs = ["arabic", "ar"];
  const englishLangs = ["english", "en"];
  if (_.includes(arabicLangs, language)) {
    language = "ar-SA";
  } else if (_.includes(englishLangs, language)) {
    language = "en-US";
  }
  return language;
}

function isArabicSpeechLanguage(language = "") {
  const value = String(language).trim().toLowerCase();
  return value === "arabic" || value === "ar" || value.startsWith("ar-") || value.startsWith("ar_");
}

function looksLikeArabic(text = "") {
  return /[\u0600-\u06FF]/.test(text);
}

function isEnglishHtml(html = "") {
  if (!html) return false;

  // Strip HTML
  const text = html
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/(p|div|li|ol|ul)>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  // Detect characters
  const hasArabic = /[\u0600-\u06FF]/.test(text);
  const hasEnglish = /[A-Za-z]/.test(text);

  // English only → true
  if (hasEnglish && !hasArabic) return true;

  return false;
}


export {
  formatDate,
  formatContact,
  numbersFormatter,
  isHTML,
  isUserChatSender,
  toLiveChatHistory,
  formatHTMLResponse,
  getDollarValue,
  getPoundValue,
  getEuroValue,
  getFixedNumber,
  getAbvValue,
  truncateString,
  translateText,
  getLanguageCode,
  isArabicSpeechLanguage,
  looksLikeArabic,
  isEnglishHtml,
};
